import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  collection,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { Calendar, dayjsLocalizer, type EventProps } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import dayjs from "dayjs";
import Autocomplete from "@mui/material/Autocomplete";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  Plus,
  SlidersHorizontal,
  Trash2,
  Upload,
} from "lucide-react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../lib/firebase";
import { useFirestoreCollectionOnce } from "../../hooks/useFirestoreCollectionOnce";
import { useDeleteSession } from "../../hooks/useSessionMutations";
import { useUpdateEvent } from "../../hooks/useEventMutations";
import { useEventParticipants } from "../../hooks/useEventParticipants";
import { useEvent } from "../../layouts/EventLayout";
import { useEventVenue } from "../../hooks/useEventVenue";
import { useSnackbar } from "../../context/SnackbarContext";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard";
import { PageHeader } from "../../components/PageHeader";
import { PhoneHeaderActions } from "../../components/phone/PhoneHeaderActions";
import type { OverflowAction } from "../../components/phone/OverflowMenu";
import { useIsPhone } from "../../hooks/useIsPhone";
import { DataTable, type Column } from "../../components/DataTable";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { FormSection } from "../../components/forms/FormSection";
import { SessionTypeManager } from "../events/SessionTypeManager";
import { ExportDialog } from "./ExportDialog";
import { ExcelSessionImportDialog } from "./ExcelSessionImportDialog";
import { exportFilename } from "../../utils/exportFilename";
import { isSharePointConfigured, getSessionsListUrl } from "../../utils/sharepoint";
import { eventSchema, type EventFormData } from "../../schemas/eventSchema";
import {
  sessionToAgendaEvent,
  type AgendaEvent,
} from "@event-planner/shared";
import type {
  Audience,
  Event,
  Person,
  Session,
  SendAgendaEmailInput,
  SendAgendaEmailResponse,
} from "@event-planner/shared";
import {
  formatAudienceLabel,
  type AudienceLabelUser,
} from "@event-planner/shared";

// ── Filter state ──────────────────────────────────────────────────────────
//
// Single filter drives both the list/timeline view AND all three export
// formats (PDF, CSV, email). Replaces the previous split where the page had
// its own filter and ExportDialog had a separate audience picker — which
// could silently diverge and leak internal sessions into a PDF the organizer
// thought was role-scoped.
//
// Audience-centric model (ADR-120 + ADR-137): role-based filtering is
// gone. Sessions reference one audience via `audienceId`; the CF-resolved
// `targetedPersonIds[]` cache is the source of truth for "who sees what".
// Person ids are uniform — pending + claimed — so the "user" filter
// matches sessions targeting a person even before they've claimed.
//
// Modes:
//   - "none"  → no filter; all sessions visible
//   - "type"  → only sessions of a given sessionTypeId
//   - "group" → only sessions whose audienceId resolves to include this group
//   - "user"  → only sessions whose targetedPersonIds contain this person

type FilterMode = "none" | "type" | "group" | "user";

interface FilterState {
  mode: FilterMode;
  typeId: string | null;
  groupId: string | null;
  user: Person | null;
}

const INITIAL_FILTER: FilterState = {
  mode: "none",
  typeId: null,
  groupId: null,
  user: null,
};

const sendAgendaEmailCallable = httpsCallable<
  SendAgendaEmailInput,
  SendAgendaEmailResponse
>(functions, "sendAgendaEmail");

const localizer = dayjsLocalizer(dayjs);

// ── Timeline card component ────────────────────────────────────────────────

function AgendaEventComponent({ event }: EventProps<AgendaEvent>) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        height: "100%",
        borderRadius: "6px",
        px: 1,
        py: 0.5,
        overflow: "hidden",
        backgroundColor: event.typeBackgroundColor,
        color: event.typeTextColor,
        cursor: "pointer",
      }}
    >
      <Typography
        sx={{
          fontFamily: theme.typography.h4.fontFamily,
          fontWeight: 600,
          lineHeight: 1.3,
          fontSize: "0.85rem",
          color: "inherit",
        }}
        noWrap
      >
        {event.title}
      </Typography>
      {event.location && (
        <Typography
          variant="caption"
          sx={{ opacity: 0.8, display: "block", lineHeight: 1.2, color: "inherit" }}
          noWrap
        >
          {event.location}
        </Typography>
      )}
      {event.speakerNames.length > 0 && (
        <Typography
          variant="caption"
          sx={{ opacity: 0.8, display: "block", lineHeight: 1.2, color: "inherit" }}
          noWrap
        >
          {event.speakerNames.join(", ")}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Phone timeline for one day: a vertical list of sessions in time order,
 * each with its time on the left and a card in the category colors. Replaces
 * the calendar grid, whose columns split too narrow when sessions overlap.
 */
function PhoneDayTimeline({
  events,
  onSelect,
}: {
  events: AgendaEvent[];
  onSelect: (event: AgendaEvent) => void;
}) {
  const sorted = [...events].sort((a, b) => a.start.getTime() - b.start.getTime());
  return (
    <Stack spacing={1.5} data-testid="phone-day-timeline">
      {sorted.map((ev) => (
        <Box
          key={ev.id}
          onClick={() => onSelect(ev)}
          sx={{ display: "flex", gap: 1.5, cursor: "pointer" }}
        >
          <Box sx={{ width: 76, flexShrink: 0, pt: 1, textAlign: "right", whiteSpace: "nowrap" }}>
            <Typography variant="body2" fontWeight={600}>
              {dayjs(ev.start).format("h:mm A")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dayjs(ev.end).format("h:mm A")}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              borderRadius: "6px",
              px: 1.5,
              py: 1,
              backgroundColor: ev.typeBackgroundColor,
              color: ev.typeTextColor,
              border: `1px solid ${ev.typeTextColor}`,
            }}
          >
            <Typography sx={{ fontWeight: 600, lineHeight: 1.3, color: "inherit" }}>
              {ev.title}
            </Typography>
            {ev.location && (
              <Typography variant="body2" sx={{ opacity: 0.85, color: "inherit" }}>
                {ev.location}
              </Typography>
            )}
            {ev.speakerNames.length > 0 && (
              <Typography variant="body2" sx={{ opacity: 0.85, color: "inherit" }}>
                {ev.speakerNames.join(", ")}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

function eventToFormData(event: Event): EventFormData {
  return {
    name: event.name,
    description: event.description,
    venueLocationId: event.venueLocationId ?? null,
    startDate: event.startDate,
    endDate: event.endDate,
    timezone: event.timezone,
    status: event.status,
    heroImageURL: event.heroImageURL,
    heroBlurhash: event.heroBlurhash,
    heroTitleOverride: event.heroTitleOverride,
    heroOverlayColor: event.heroOverlayColor,
    heroOverlayOpacity: event.heroOverlayOpacity,
    heroOverlayDirection: event.heroOverlayDirection,
    config: event.config,
  };
}

function AgendaCategoriesPanel({
  event,
  onSaved,
}: {
  event: Event;
  onSaved: () => void;
}) {
  const { update, loading } = useUpdateEvent();
  const { showSuccess, showError } = useSnackbar();
  const form = useForm<EventFormData>({
    resolver: zodResolver(eventSchema),
    defaultValues: eventToFormData(event),
  });

  const { dialog: unsavedGuardDialog } = useUnsavedChangesGuard(
    form.formState.isDirty,
    form.formState.isSubmitting || loading,
  );

  useEffect(() => {
    form.reset(eventToFormData(event));
  }, [event, form]);

  async function handleSubmit(data: EventFormData) {
    try {
      await update(event.id, { config: data.config });
      showSuccess("Categories updated");
      onSaved();
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to save categories");
    }
  }

  return (
    <FormProvider {...form}>
      {unsavedGuardDialog}
      <Box
        component="form"
        onSubmit={form.handleSubmit(handleSubmit)}
        sx={{ maxWidth: 1100 }}
      >
        <FormSection title="Categories">
          <SessionTypeManager eventId={event.id} />
        </FormSection>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={18} /> : undefined}
          >
            {loading ? "Saving..." : "Save Categories"}
          </Button>
        </Box>
      </Box>
    </FormProvider>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

type ViewMode = "list" | "timeline" | "categories";

export function SessionListPage() {
  const isPhone = useIsPhone();
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { event, refetch: refetchEvent } = useEvent();
  const { venue: eventVenue } = useEventVenue(event);
  const { showSuccess, showError } = useSnackbar();
  const { remove, loading: deleting } = useDeleteSession();
  const theme = useTheme();

  const [deleteTarget, setDeleteTarget] = useState<Session | null>(null);
  const [filter, setFilter] = useState<FilterState>(INITIAL_FILTER);
  const [view, setView] = useState<ViewMode>("list");
  const [selectedDateIndex, setSelectedDateIndex] = useState(0);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const pdfRenderRef = useRef<HTMLDivElement>(null);

  const sessionsQuery = useMemo(
    () =>
      eventId
        ? query(
            collection(db, "sessions"),
            where("eventId", "==", eventId),
            orderBy("date", "asc"),
            orderBy("startTime", "asc"),
          )
        : null,
    [eventId],
  );

  const {
    data: sessions,
    loading,
    refetch: refetchSessions,
  } = useFirestoreCollectionOnce<Session>(sessionsQuery);

  const { persons: eventMembers, participants } = useEventParticipants(eventId);

  // All event audiences — named ones surface in the filter UI; the full
  // map (named + anonymous + personal) is what `formatAudienceLabel`
  // walks when rendering an anonymous composition's includes.
  const audiencesQuery = useMemo(
    () =>
      eventId
        ? query(
            collection(db, "audiences"),
            where("eventId", "==", eventId),
          )
        : null,
    [eventId],
  );
  const { data: audiences } =
    useFirestoreCollectionOnce<Audience>(audiencesQuery);

  const audienceById = useMemo(
    () => new Map(audiences.map((a) => [a.id, a])),
    [audiences],
  );

  // Filter dropdown shows only named audiences (custom groups + role
  // audiences). Anonymous compositions and personal audiences are hidden
  // from the picker per ADR-120.
  const namedAudiences = useMemo(
    () =>
      audiences
        .filter((a) => a.name !== null)
        .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
    [audiences],
  );

  const userById = useMemo<Map<string, AudienceLabelUser>>(
    () =>
      new Map(
        eventMembers.map((p) => [p.id, { id: p.id, displayName: p.displayName }]),
      ),
    [eventMembers],
  );
  // ADR-136: persons hold email directly, no separate userProfiles fetch.

  const sessionTypes = event?.config.sessionTypes ?? [];

  function getSessionType(sessionTypeId: string) {
    return sessionTypes.find((st) => st.id === sessionTypeId);
  }

  const filteredSessions = useMemo(() => {
    switch (filter.mode) {
      case "none":
        return sessions;
      case "type":
        if (!filter.typeId) return sessions;
        return sessions.filter((s) => s.sessionTypeId === filter.typeId);
      case "group": {
        if (!filter.groupId) return sessions;
        const groupId = filter.groupId;
        // A session matches when its audienceId equals the picked named
        // audience, OR when its audience is an anonymous composition that
        // includes the picked audience. Anonymous-as-leaves is enforced
        // server-side, so the walk only descends one level.
        return sessions.filter((s) => {
          if (!s.audienceId) return false;
          if (s.audienceId === groupId) return true;
          const aud = audienceById.get(s.audienceId);
          if (!aud) return false;
          if (aud.name === null && aud.system === false) {
            return aud.members.includedAudienceIds.includes(groupId);
          }
          return false;
        });
      }
      case "user":
        if (!filter.user) return sessions;
        return sessions.filter((s) =>
          s.targetedPersonIds.includes(filter.user!.id),
        );
    }
  }, [sessions, filter, audienceById]);

  // ── Export driver ──
  // Summary string shown in the export dialog so the organizer can see what
  // they're about to export at a glance.
  const filterSummary = useMemo((): string => {
    switch (filter.mode) {
      case "none":
        return "All sessions";
      case "type": {
        const st = sessionTypes.find((t) => t.id === filter.typeId);
        return st ? `Category: ${st.name}` : "All sessions";
      }
      case "group": {
        const a = namedAudiences.find((aa) => aa.id === filter.groupId);
        return a?.name ? `Group: ${a.name}` : "All sessions";
      }
      case "user":
        return filter.user ? `User: ${filter.user.displayName}` : "All sessions";
    }
    // Fallback for exhaustiveness
    return "All sessions";
  }, [filter, sessionTypes, namedAudiences]);

  // ── Group sessions by date for the list view ──
  const sessionsByDate = useMemo(() => {
    const groups: { date: string; sessions: Session[] }[] = [];
    let currentDate = "";
    for (const session of filteredSessions) {
      const d = session.date || "unknown";
      if (d !== currentDate) {
        currentDate = d;
        groups.push({ date: d, sessions: [] });
      }
      groups[groups.length - 1].sessions.push(session);
    }
    return groups;
  }, [filteredSessions]);

  // ── Timeline data ──

  const agendaEvents = useMemo(
    () => filteredSessions.map((s) => sessionToAgendaEvent(s, sessionTypes)),
    [filteredSessions, sessionTypes],
  );

  const uniqueDates = useMemo(() => {
    const dates = [...new Set(filteredSessions.map((s) => s.date))].filter(Boolean).sort();
    return dates;
  }, [filteredSessions]);

  const selectedDate = uniqueDates[selectedDateIndex]
    ? new Date(uniqueDates[selectedDateIndex] + "T00:00:00")
    : new Date();

  const dateFilteredAgendaEvents = useMemo(() => {
    const dateStr = uniqueDates[selectedDateIndex];
    if (!dateStr) return agendaEvents;
    return agendaEvents.filter((e) => dayjs(e.start).format("YYYY-MM-DD") === dateStr);
  }, [agendaEvents, uniqueDates, selectedDateIndex]);

  // Compute dynamic min/max for the timeline — at least 1 hour of padding
  // before the earliest session and after the latest, rounded to full hours.
  //
  // Guards against three edge cases that would otherwise break
  // react-big-calendar's getSlotMetrics with "Invalid array length":
  //   1. Cross-midnight sessions (end < start hour-of-day)
  //   2. Sessions ending at 23:xx (endHour rounds up to 24, which when
  //      passed as `new Date(0,0,0,24,0)` overflows into the next day,
  //      making max-time-of-day appear earlier than min)
  //   3. NaN / non-finite hours from invalid start/end dates
  const DEFAULT_MIN_HOUR = 7;
  const DEFAULT_MAX_HOUR = 23;
  const { timelineMin, timelineMax } = useMemo(() => {
    const fallback = {
      timelineMin: new Date(0, 0, 0, DEFAULT_MIN_HOUR, 0),
      timelineMax: new Date(0, 0, 0, DEFAULT_MAX_HOUR, 0),
    };
    if (dateFilteredAgendaEvents.length === 0) return fallback;

    let earliestHour = 24;
    let latestHour = 0;

    for (const ev of dateFilteredAgendaEvents) {
      const startHour = ev.start.getHours();
      const endMinutes = ev.end.getMinutes();
      const rawEndHour = ev.end.getHours() + (endMinutes > 0 ? 1 : 0);
      // Cross-midnight: snap end to the end of the selected day so the
      // session is at least visible up to 23:59 instead of producing an
      // hour earlier than the start.
      const endHour = rawEndHour < startHour ? 24 : rawEndHour;

      if (Number.isFinite(startHour) && startHour < earliestHour) {
        earliestHour = startHour;
      }
      if (Number.isFinite(endHour) && endHour > latestHour) {
        latestHour = endHour;
      }
    }

    if (!Number.isFinite(earliestHour) || !Number.isFinite(latestHour)) {
      return fallback;
    }

    const minHour = Math.max(0, Math.min(earliestHour - 1, 22));
    const rawMaxHour = Math.max(minHour + 1, Math.min(24, latestHour + 1));
    // Clamp the max into the same calendar day. Hour 24 would overflow
    // `new Date(0,0,0,24,0)` into Jan 1 1900, breaking RBC's
    // time-of-day-on-selected-day interpretation. End-of-day is the
    // safe representation.
    const timelineMax =
      rawMaxHour >= 24
        ? new Date(0, 0, 0, 23, 59, 59)
        : new Date(0, 0, 0, rawMaxHour, 0);

    return {
      timelineMin: new Date(0, 0, 0, minHour, 0),
      timelineMax,
    };
  }, [dateFilteredAgendaEvents]);

  // Height sized to fit exactly — no internal scroll needed.
  // Each timeslot group (1 hour) renders at 60px (minHeight in sx below).
  // Use minutes-based diff so the 23:59:59 end-of-day case still produces
  // a sensible hour count.
  const timelineHours = Math.max(
    1,
    Math.round(
      (timelineMax.getTime() - timelineMin.getTime()) / (60 * 60 * 1000),
    ),
  );
  const timelineHeight = timelineHours * 60;

  const handleSelectEvent = useCallback(
    (ev: AgendaEvent) => navigate(ev.id),
    [navigate],
  );

  // ── Handlers ──

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await remove(deleteTarget.eventId, deleteTarget.id);
      showSuccess("Session deleted");
      setDeleteTarget(null);
      refetchSessions();
    } catch {
      showError("Failed to delete session");
    }
  }

  async function handleExportPdf() {
    if (!pdfRenderRef.current || !event) return;
    setExporting(true);
    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - margin * 2;
      let cursorY = margin;

      const children = Array.from(pdfRenderRef.current.children) as HTMLElement[];
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        const canvas = await html2canvas(child, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
        });
        const imgData = canvas.toDataURL("image/png");
        const imgHeight = (canvas.height * contentWidth) / canvas.width;

        if (cursorY + imgHeight > pageHeight - margin && cursorY > margin) {
          pdf.addPage();
          cursorY = margin;
        }

        pdf.addImage(imgData, "PNG", margin, cursorY, contentWidth, imgHeight);
        cursorY += imgHeight;
      }

      const filename = exportFilename({
        eventName: event.name,
        kind: "agenda",
        ext: "pdf",
      });
      pdf.save(filename);
    } catch {
      showError("Failed to generate PDF");
    } finally {
      setExporting(false);
    }
  }

  async function handleSendEmail(target: string) {
    if (!eventId) return;
    const res = await sendAgendaEmailCallable({ eventId, target });
    showSuccess(
      `Agenda emails sent to ${res.data.recipientCount} recipient${res.data.recipientCount === 1 ? "" : "s"}`,
    );
  }

  function formatTime(ts: Session["startTime"]): string {
    if (!ts || typeof ts !== "object" || !("toDate" in ts)) return "—";
    return dayjs(ts.toDate()).format("h:mm A");
  }

  const columns: Column<Session>[] = [
    {
      id: "title",
      label: "Title",
      width: "40%",
      render: (session) => session.title,
    },
    {
      id: "type",
      label: "Category",
      width: "15%",
      phone: "chips",
      render: (session) => {
        const st = getSessionType(session.sessionTypeId);
        return (
          <Chip
            label={st?.name ?? session.sessionTypeId}
            size="small"
            sx={
              st
                ? {
                    backgroundColor: st.backgroundColor,
                    color: st.textColor,
                    fontWeight: 500,
                  }
                : undefined
            }
          />
        );
      },
    },
    {
      id: "time",
      label: "Time",
      width: "20%",
      phone: "secondary",
      render: (session) =>
        `${formatTime(session.startTime)} – ${formatTime(session.endTime)}`,
    },
    {
      id: "location",
      label: "Location",
      width: "13%",
      phone: "secondary",
      render: (session) => session.locationLabel ?? "—",
    },
    {
      id: "audience",
      label: "Audience",
      width: "20%",
      phone: "chips",
      render: (session) => {
        if (!session.audienceId) {
          return (
            <Typography variant="caption" color="text.secondary">—</Typography>
          );
        }
        const audience = audienceById.get(session.audienceId);
        return (
          <Chip
            label={formatAudienceLabel(audience, audienceById, userById)}
            size="small"
            variant="outlined"
          />
        );
      },
    },
    {
      id: "actions",
      label: "",
      width: "10%",
      phone: "hidden",
      align: "right",
      render: (session) => (
        <Box sx={{ display: "inline-flex", gap: 0.5 }}>
          <Tooltip title="Duplicate session">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                // Opens the create form pre-filled from this session
                // (incl. speakers + materials); nothing is written
                // until the organizer saves.
                navigate(`new?from=${session.id}`);
              }}
              aria-label={`Duplicate ${session.title}`}
              data-testid="duplicate-session-button"
            >
              <Copy size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete session">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTarget(session);
              }}
              aria-label={`Delete ${session.title}`}
            >
              <Trash2 size={18} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // Filter controls: in the header on desktop; in a bottom sheet on phones.
  const filterControls = (
    <>
              {/* ── Filter mode ── */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={filter.mode}
                  onChange={(e) => {
                    const mode = e.target.value as FilterMode;
                    // Reset all value slots when mode changes — stale values
                    // from a prior mode shouldn't leak into the new one.
                    setFilter({
                      mode,
                      typeId: null,
                      groupId: null,
                      user: null,
                    });
                  }}
                  data-testid="filter-mode-select"
                >
                  <MenuItem value="none">All sessions</MenuItem>
                  <MenuItem value="type">Filter by category</MenuItem>
                  <MenuItem value="group">Filter by group</MenuItem>
                  <MenuItem value="user">Filter by user</MenuItem>
                </Select>
              </FormControl>

              {/* ── Filter value — shape depends on mode ── */}
              {filter.mode === "type" && (
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <Select
                    value={filter.typeId ?? ""}
                    onChange={(e) =>
                      setFilter((prev) => ({
                        ...prev,
                        typeId: e.target.value || null,
                      }))
                    }
                    displayEmpty
                    data-testid="filter-type-select"
                  >
                    <MenuItem value="" disabled>
                      Select category…
                    </MenuItem>
                    {sessionTypes.map((st) => (
                      <MenuItem key={st.id} value={st.id}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              backgroundColor: st.backgroundColor,
                              flexShrink: 0,
                            }}
                          />
                          {st.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {filter.mode === "group" && (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <Select
                    value={filter.groupId ?? ""}
                    onChange={(e) =>
                      setFilter((prev) => ({
                        ...prev,
                        groupId: e.target.value || null,
                      }))
                    }
                    displayEmpty
                    data-testid="filter-group-select"
                  >
                    <MenuItem value="" disabled>
                      Select group…
                    </MenuItem>
                    {namedAudiences.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {a.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {filter.mode === "user" && (
                <Autocomplete<Person, false>
                  size="small"
                  options={eventMembers}
                  value={filter.user}
                  onChange={(_, value) =>
                    setFilter((prev) => ({ ...prev, user: value }))
                  }
                  getOptionLabel={(user) => user.displayName}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  filterOptions={(options, { inputValue }) => {
                    const q = inputValue.toLowerCase();
                    if (!q) return options;
                    return options.filter(
                      (u) =>
                        u.displayName.toLowerCase().includes(q) ||
                        (u.firstName ?? "").toLowerCase().includes(q) ||
                        (u.lastName ?? "").toLowerCase().includes(q),
                    );
                  }}
                  renderOption={(props, user) => {
                    const { key, ...rest } = props;
                    return (
                      <li key={key} {...rest}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar
                            src={user.photoURL ?? undefined}
                            sx={{ width: 24, height: 24 }}
                          >
                            {user.firstName?.[0] || "?"}
                          </Avatar>
                          <Typography variant="body2">{user.displayName}</Typography>
                        </Stack>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Search attendees..." />
                  )}
                  sx={{ minWidth: 220 }}
                  noOptionsText="No matching attendees"
                  data-testid="filter-user-autocomplete"
                />
              )}
    </>
  );

  // Phone: duplicate and delete move from row icons to each row's ⋮ menu.
  function sessionRowActions(session: Session): OverflowAction[] {
    return [
      {
        label: "Duplicate",
        icon: <Copy size={18} />,
        onClick: () => navigate(`new?from=${session.id}`),
        testId: "duplicate-session-button",
      },
      {
        label: "Delete",
        icon: <Trash2 size={18} />,
        onClick: () => setDeleteTarget(session),
        destructive: true,
      },
    ];
  }

  return (
    <Box data-testid="agenda-page">
      <PageHeader
        title="Agenda"
        action={
          view === "categories" ? undefined : isPhone ? (
            <PhoneHeaderActions
              primary={
                <Button
                  variant="contained"
                  startIcon={<Plus size={18} />}
                  onClick={() => navigate("new")}
                  data-testid="new-session-button"
                >
                  New
                </Button>
              }
              more={[
                {
                  label: "Import",
                  icon: <Upload size={18} />,
                  onClick: () => setImportDialogOpen(true),
                  testId: "import-sessions-button",
                },
                {
                  label: "Export",
                  icon: <Download size={18} />,
                  onClick: () => setExportDialogOpen(true),
                  disabled: exporting,
                  testId: "export-button",
                },
              ]}
            />
          ) : (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              {filterControls}
              <Button
                variant="outlined"
                startIcon={<Upload size={16} />}
                onClick={() => setImportDialogOpen(true)}
                data-testid="import-sessions-button"
              >
                Import
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download size={18} />}
                onClick={() => setExportDialogOpen(true)}
                disabled={exporting}
                data-testid="export-button"
              >
                Export
              </Button>
              <Button
                variant="contained"
                startIcon={<Plus size={18} />}
                onClick={() => navigate("new")}
                data-testid="new-session-button"
              >
                Create Session
              </Button>
            </Box>
          )
        }
      />

      {/* View toggle tabs */}
      <Tabs
        value={view}
        onChange={(_, v) => setView(v as ViewMode)}
        sx={{ mb: 2 }}
      >
        <Tab label="Sessions" value="list" disableRipple />
        <Tab label="Timeline" value="timeline" disableRipple />
        <Tab label="Categories" value="categories" disableRipple />
      </Tabs>

      {isPhone && view !== "categories" && (
        <>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<SlidersHorizontal size={16} />}
              onClick={() => setFilterSheetOpen(true)}
              data-testid="phone-filter-button"
            >
              Filter
            </Button>
            {filterSummary !== "All sessions" && (
              <Chip
                label={filterSummary}
                onDelete={() => setFilter(INITIAL_FILTER)}
                data-testid="phone-filter-active"
                sx={{ minWidth: 0 }}
              />
            )}
          </Stack>
          <Drawer
            anchor="bottom"
            open={filterSheetOpen}
            onClose={() => setFilterSheetOpen(false)}
            slotProps={{
              paper: {
                sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, px: 2, pt: 2, pb: 3 },
              },
            }}
          >
            <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
              Filter sessions
            </Typography>
            <Stack
              spacing={2}
              sx={{
                "& .MuiFormControl-root, & .MuiAutocomplete-root": { width: "100%" },
              }}
            >
              {filterControls}
              <Button variant="contained" onClick={() => setFilterSheetOpen(false)}>
                Done
              </Button>
            </Stack>
          </Drawer>
        </>
      )}

      {view === "categories" ? (
        event ? (
          <AgendaCategoriesPanel event={event} onSaved={refetchEvent} />
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        )
      ) : loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : sessions.length === 0 ? (
        <Typography color="text.secondary">
          No sessions yet. Create your first session to build the agenda.
        </Typography>
      ) : view === "list" ? (
        /* ── List view — grouped by date ── */
        filteredSessions.length === 0 ? (
          <Typography color="text.secondary">
            No sessions match the selected filter.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {sessionsByDate.map((group) => (
              <Box key={group.date}>
                <Typography
                  variant="h6"
                  sx={{ mb: 1, fontWeight: 600 }}
                >
                  {group.date !== "unknown"
                    ? dayjs(group.date).format("dddd, MMMM D")
                    : "No Date"}
                </Typography>
                <DataTable
                  columns={columns}
                  data={group.sessions}
                  loading={false}
                  ariaLabel={`Sessions for ${group.date}`}
                  getRowKey={(session) => session.id}
                  onRowClick={(session) => navigate(session.id)}
                  phoneRowActions={sessionRowActions}
                  phoneRowActionsLabel={(session) => `More actions for ${session.title}`}
                />
              </Box>
            ))}
          </Box>
        )
      ) : (
        /* ── Timeline view ── */
        <>
          {uniqueDates.length === 0 ? (
            <Typography color="text.secondary">
              No sessions match the selected filter.
            </Typography>
          ) : (
            <>
              {/* Day navigation */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                  gap: 1,
                }}
              >
                <IconButton
                  onClick={() => setSelectedDateIndex((i) => Math.max(0, i - 1))}
                  disabled={selectedDateIndex === 0}
                  aria-label="Previous day"
                  size="small"
                >
                  <ChevronLeft size={20} />
                </IconButton>

                <Tabs
                  value={Math.min(selectedDateIndex, uniqueDates.length - 1)}
                  onChange={(_, v) => setSelectedDateIndex(v as number)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ flex: 1 }}
                >
                  {uniqueDates.map((date) => (
                    <Tab
                      key={date}
                      label={dayjs(date).format("ddd, MMM D")}
                    />
                  ))}
                </Tabs>

                <IconButton
                  onClick={() => setSelectedDateIndex((i) => Math.min(uniqueDates.length - 1, i + 1))}
                  disabled={selectedDateIndex >= uniqueDates.length - 1}
                  aria-label="Next day"
                  size="small"
                >
                  <ChevronRight size={20} />
                </IconButton>
              </Box>

              {/* Calendar — no inner scroll; the page itself scrolls */}
              {isPhone ? (
                <PhoneDayTimeline
                  events={dateFilteredAgendaEvents}
                  onSelect={handleSelectEvent}
                />
              ) : (
              <Box
                sx={{
                  height: timelineHeight,
                  "& .rbc-calendar": {
                    fontFamily: theme.typography.fontFamily,
                  },
                  "& .rbc-time-view": {
                    borderColor: theme.palette.divider,
                    overflow: "visible",
                  },
                  "& .rbc-time-header": {
                    display: "none",
                  },
                  "& .rbc-time-content": {
                    borderTop: `1px solid ${theme.palette.divider}`,
                    overflow: "visible",
                    maxHeight: "none",
                  },
                  "& .rbc-timeslot-group": {
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    minHeight: 60,
                  },
                  "& .rbc-time-slot .rbc-label": {
                    color: theme.palette.text.secondary,
                    fontSize: "0.75rem",
                  },
                  "& .rbc-day-slot .rbc-time-slot": {
                    borderTop: "none",
                  },
                  "& .rbc-event": {
                    backgroundColor: "transparent",
                    border: "none",
                    padding: 0,
                    borderRadius: 4,
                  },
                  "& .rbc-event-label": {
                    display: "none",
                  },
                  "& .rbc-current-time-indicator": {
                    backgroundColor: theme.palette.error.main,
                    height: 2,
                  },
                  "& .rbc-today": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                <Calendar<AgendaEvent>
                  localizer={localizer}
                  events={dateFilteredAgendaEvents}
                  date={selectedDate}
                  view="day"
                  views={["day"]}
                  onNavigate={() => {}}
                  toolbar={false}
                  components={{
                    event: AgendaEventComponent,
                  }}
                  onSelectEvent={handleSelectEvent}
                  step={30}
                  timeslots={2}
                  min={timelineMin}
                  max={timelineMax}
                  eventPropGetter={(ev) => ({
                    style: {
                      backgroundColor: "transparent",
                      border: `1px solid ${(ev as AgendaEvent).typeTextColor}`,
                      borderRadius: 6,
                      padding: 0,
                    },
                  })}
                />
              </Box>
              )}
            </>
          )}
        </>
      )}

      {eventId && event && (
        <ExcelSessionImportDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          eventId={eventId}
          timezone={event.timezone}
          audiences={audiences}
          onImported={() => {
            refetchEvent();
            refetchSessions();
          }}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Session"
        message={
          deleteTarget
            ? `Permanently delete "${deleteTarget.title}"? This also removes all speaker links, attendee assignments, RSVPs, private notes, check-ins, surveys, and survey responses for this session. This cannot be undone.`
            : ""
        }
        loading={deleting}
      />

      {event && (
        <ExportDialog
          open={exportDialogOpen}
          onClose={() => setExportDialogOpen(false)}
          event={event}
          sessions={filteredSessions}
          participants={participants}
          persons={eventMembers}
          audienceById={audienceById}
          userById={userById}
          filter={filter}
          filterSummary={filterSummary}
          onExportPdf={handleExportPdf}
          onSendEmail={handleSendEmail}
          sharePointUrl={
            isSharePointConfigured() ? getSessionsListUrl(event.name) : null
          }
        />
      )}

      {/* Hidden render target for PDF export */}
      <Box
        ref={pdfRenderRef}
        sx={{
          position: "absolute",
          left: -9999,
          top: 0,
          width: 800,
          backgroundColor: "#fff",
          padding: "32px",
          fontFamily: "'Inter', Arial, sans-serif",
        }}
      >
        {event && (
          <>
            {/* Header + color key — captured as one block for PDF pagination */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h3"
                sx={{ mb: 0.5, color: "#21376C" }}
              >
                {event.name}
              </Typography>
              <Typography variant="body2" sx={{ color: "#555", mb: 0.5 }}>
                {event.startDate === event.endDate
                  ? dayjs(event.startDate).format("MMMM D, YYYY")
                  : `${dayjs(event.startDate).format("MMMM D")} – ${dayjs(event.endDate).format("MMMM D, YYYY")}`}
              </Typography>
              <Typography variant="body2" sx={{ color: "#555", mb: 1.5 }}>
                {[eventVenue?.label, eventVenue?.city].filter(Boolean).join(", ")}
              </Typography>

              {/* Category color key */}
              {sessionTypes.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    p: "8px 12px",
                    borderRadius: "6px",
                    backgroundColor: "#F5F5F5",
                    border: "1px solid #E0E0E0",
                  }}
                >
                  {sessionTypes.map((st) => (
                    <Box
                      key={st.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: "3px",
                          backgroundColor: st.backgroundColor,
                          border: `1px solid ${st.textColor}`,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "11px",
                          color: "#333",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {st.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {sessionsByDate.map((group) => (
              <Box key={group.date} sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#21376C",
                    fontWeight: 600,
                    borderBottom: "2px solid #21376C",
                    pb: 0.5,
                    mb: 1,
                  }}
                >
                  {group.date !== "unknown"
                    ? dayjs(group.date).format("dddd, MMMM D")
                    : "No Date"}
                </Typography>
                {group.sessions.map((session) => {
                  const st = getSessionType(session.sessionTypeId);
                  return (
                    <Box
                      key={session.id}
                      sx={{
                        mb: 0.75,
                        p: "8px 12px",
                        borderRadius: "6px",
                        backgroundColor: st?.backgroundColor ?? "#9E9E9E",
                        border: `1px solid ${st?.textColor ?? "#FFF"}`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: st?.textColor ?? "#FFF",
                        }}
                      >
                        {session.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: st?.textColor ?? "#FFF",
                          opacity: 0.85,
                        }}
                      >
                        {formatTime(session.startTime)} – {formatTime(session.endTime)}
                        {session.locationLabel ? ` · ${session.locationLabel}` : ""}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            ))}
          </>
        )}
      </Box>
    </Box>
  );
}
